import React, { useState, useEffect, useRef, useCallback } from "react";
import { Lock, LogOut, Search, Camera, Image as ImageIcon, RotateCcw, Send, CheckCircle2, Loader2, X, ShieldAlert } from "lucide-react";
import BrandLockup from "./BrandLockup";
import useNoIndex from "../hooks/useNoIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uuid = () => (window.crypto && crypto.randomUUID ? crypto.randomUUID()
  : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0; return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    }));

const KINDS = [
  { id: "front", label: "Front" },
  { id: "rear", label: "Rear / Ports" },
  { id: "detail", label: "Detail" },
  { id: "label", label: "Label / Serial", private: true },
];

function xhrUpload(url, formData, headers, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let data = {}; try { data = JSON.parse(xhr.responseText); } catch (_) {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data.detail || "Upload failed"));
    };
    xhr.onerror = () => reject(new Error("Network error — please retry"));
    xhr.send(formData);
  });
}

export default function WorkerPhotos() {
  useNoIndex();
  const [pin, setPin] = useState(sessionStorage.getItem("kapatia_photo_pin") || "");
  const [verified, setVerified] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pin) return;
    fetch(`${API}/photos/verify_pin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pin }) })
      .then((r) => { if (!r.ok) { sessionStorage.removeItem("kapatia_photo_pin"); setPin(""); setVerified(false); } else setVerified(true); })
      .catch(() => {});
  }, [pin]);

  const login = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/photos/verify_pin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pin: input }) });
      if (!r.ok) { setError("Incorrect PIN. Try again."); return; }
      sessionStorage.setItem("kapatia_photo_pin", input); setPin(input); setVerified(true);
    } catch { setError("Could not reach server."); } finally { setBusy(false); }
  };
  const logout = () => { sessionStorage.removeItem("kapatia_photo_pin"); setPin(""); setVerified(false); setInput(""); };

  if (!verified) {
    return (
      <div className="grid min-h-screen place-items-center p-5" style={{ background: "var(--cream)" }}>
        <form onSubmit={login} data-testid="photo-pin-form" className="w-full max-w-xs rounded-2xl border p-7 text-center"
              style={{ background: "var(--cream-2)", borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl" style={{ background: "var(--green-100)", color: "var(--green-800)" }}><Camera size={22} /></span>
          <h1 className="mt-4 font-heading text-[22px] font-bold" style={{ color: "var(--green-950)" }}>Godown Photos</h1>
          <p className="mt-1 text-[13.5px]" style={{ color: "var(--muted)" }}>Enter the staff PIN to add product photos</p>
          <input type="password" inputMode="numeric" maxLength={4} value={input} autoFocus data-testid="photo-pin-input"
                 onChange={(e) => setInput(e.target.value.replace(/\D/g, ""))} placeholder="••••"
                 className="mt-5 w-full rounded-lg border bg-white px-3.5 py-3 text-center text-[24px] tracking-[0.5em] outline-none focus:ring-4"
                 style={{ borderColor: "#d8c8af", color: "var(--green-950)" }} />
          {error && <p className="mt-2 text-[13px] font-semibold" data-testid="photo-pin-error" style={{ color: "var(--orange-700)" }}>{error}</p>}
          <button type="submit" disabled={busy || input.length !== 4} data-testid="photo-pin-submit"
                  className="mt-4 w-full rounded-full py-3 font-heading font-bold text-white disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>Unlock</button>
        </form>
      </div>
    );
  }
  return <WorkerCapture pin={pin} onLogout={logout} />;
}

function WorkerCapture({ pin, onLogout }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [product, setProduct] = useState(null);
  const [kind, setKind] = useState("front");
  const [pending, setPending] = useState(null); // {dataUrl, file}
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState("");
  const [submitted, setSubmitted] = useState([]);
  const [error, setError] = useState("");
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [token, setToken] = useState("");
  const headers = { "X-Photo-Pin": pin };

  useEffect(() => {
    fetch(`${API}/photos/access_token`, { headers }).then((r) => r.json()).then((d) => setToken(d.token || "")).catch(() => {});
  }, [pin]);

  const search = useCallback(async (term) => {
    try {
      const r = await fetch(`${API}/photos/products/search?q=${encodeURIComponent(term)}`, { headers });
      const d = await r.json();
      setResults(Array.isArray(d) ? d : []);
    } catch { setResults([]); }
  }, [pin]);

  useEffect(() => { const t = setTimeout(() => { if (!product) search(q); }, 250); return () => clearTimeout(t); }, [q, product, search]);

  const loadMine = useCallback(async (pid) => {
    try { const r = await fetch(`${API}/photos/mine?product_id=${pid}`, { headers }); const d = await r.json(); setSubmitted(Array.isArray(d) ? d : []); }
    catch { setSubmitted([]); }
  }, [pin]);

  const pickProduct = (p) => { setProduct(p); setResults([]); setQ(""); setPending(null); setError(""); loadMine(p.id); };

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPending({ dataUrl: reader.result, file: f, token: uuid() });
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const submit = async () => {
    if (!pending || !product) return;
    setUploading(true); setError(""); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("product_id", product.id);
      fd.append("kind", kind);
      fd.append("upload_token", pending.token); // same token on retry => no duplicate
      fd.append("file", pending.file, pending.file.name || "photo.jpg");
      await xhrUpload(`${API}/photos/upload`, fd, headers, setProgress);
      setToast("Saved ✓"); setTimeout(() => setToast(""), 1800);
      setPending(null);
      loadMine(product.id);
    } catch (err) { setError(err.message); } finally { setUploading(false); }
  };

  const grouped = KINDS.map((k) => ({ ...k, items: submitted.filter((s) => s.kind === k.id) }));

  return (
    <div className="min-h-screen" style={{ background: "var(--cream)" }}>
      <header className="border-b" style={{ background: "var(--cream-2)", borderColor: "var(--line)" }}>
        <div className="mx-auto flex max-w-lg items-center justify-between px-5 py-3">
          <BrandLockup nameSize="text-[17px]" hindiSize="text-[16px]" citySize="text-[8.5px]" />
          <button onClick={onLogout} data-testid="photo-logout" className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 font-heading text-[12.5px] font-bold" style={{ borderColor: "var(--line)", color: "var(--green-900)" }}><LogOut size={14} /> Lock</button>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-5 py-5 pb-20">
        <h1 className="mb-1 font-heading text-[20px] font-bold" style={{ color: "var(--green-950)" }}>Add product photos</h1>
        <p className="mb-4 text-[13px]" style={{ color: "var(--muted)" }}>Search the product code, then take or choose photos. This page is for photos only.</p>

        {!product ? (
          <div>
            <div className="flex items-center gap-2 rounded-full border bg-white px-4 py-3" style={{ borderColor: "var(--line)" }}>
              <Search size={17} style={{ color: "var(--muted)" }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="photo-search-input"
                     placeholder="Search product code or name…" className="w-full bg-transparent text-[15px] outline-none" />
            </div>
            <div className="mt-3 grid gap-2" data-testid="photo-search-results">
              {results.map((p) => (
                <button key={p.id} onClick={() => pickProduct(p)} data-testid={`photo-pick-${p.code}`}
                        className="flex items-center justify-between gap-3 rounded-xl border bg-white p-3 text-left"
                        style={{ borderColor: "var(--line)" }}>
                  <span className="min-w-0">
                    <span className="font-heading text-[13px] font-extrabold" style={{ color: "var(--orange-700)" }}>{p.code || "—"}</span>
                    <span className="block truncate text-[13.5px] font-semibold" style={{ color: "var(--green-950)" }}>{p.name}</span>
                    <span className="text-[12px]" style={{ color: "var(--muted)" }}>{p.category}</span>
                  </span>
                  {p.photo_count > 0 && <span className="shrink-0 rounded-full px-2 py-1 text-[11px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-800)" }}>{p.photo_count} photo{p.photo_count > 1 ? "s" : ""}</span>}
                </button>
              ))}
              {q && results.length === 0 && <p className="py-6 text-center text-[13px]" style={{ color: "var(--muted)" }}>No matching product.</p>}
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "var(--line)" }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-heading text-[14px] font-extrabold" style={{ color: "var(--orange-700)" }}>{product.code || "—"}</span>
                  <p className="text-[14px] font-semibold" style={{ color: "var(--green-950)" }}>{product.name}</p>
                  <p className="text-[12.5px]" style={{ color: "var(--muted)" }}>{product.category}{product.brand ? ` · ${product.brand}` : ""}</p>
                </div>
                <button onClick={() => { setProduct(null); setPending(null); }} data-testid="photo-change-product" className="shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-bold" style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>Change</button>
              </div>
            </div>

            <div>
              <p className="mb-2 font-heading text-[13px] font-bold" style={{ color: "var(--green-950)" }}>Photo type</p>
              <div className="grid grid-cols-2 gap-2">
                {KINDS.map((k) => (
                  <button key={k.id} onClick={() => setKind(k.id)} data-testid={`photo-kind-${k.id}`}
                          className="rounded-xl border-2 px-3 py-2.5 font-heading text-[13px] font-bold"
                          style={kind === k.id ? { borderColor: "var(--green-800)", background: "var(--green-900)", color: "#fff" } : { borderColor: "var(--line)", color: "var(--green-900)", background: "#fff" }}>
                    {k.label}{k.private && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold" style={{ color: kind === k.id ? "var(--yellow-500)" : "var(--orange-700)" }}><ShieldAlert size={11} />private</span>}
                  </button>
                ))}
              </div>
              {kind === "label" && <p className="mt-1.5 text-[12px]" style={{ color: "var(--orange-700)" }}>Label / serial photos stay private — never shown on the website.</p>}
            </div>

            <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="photo-camera-input" />
            <input ref={galRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="photo-gallery-input" />

            {!pending ? (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => camRef.current?.click()} data-testid="photo-take" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-6 font-heading text-[14px] font-bold" style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.6)" }}><Camera size={19} style={{ color: "#E8501A" }} /> Take Photo</button>
                <button onClick={() => galRef.current?.click()} data-testid="photo-gallery" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-6 font-heading text-[14px] font-bold" style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.6)" }}><ImageIcon size={19} style={{ color: "#E8501A" }} /> Choose from Gallery</button>
              </div>
            ) : (
              <div className="rounded-2xl border bg-white p-3" style={{ borderColor: "var(--line)" }}>
                <img src={pending.dataUrl} alt="Preview" data-testid="photo-preview" className="mb-3 max-h-72 w-full rounded-lg object-contain" style={{ background: "#f3efe6" }} />
                {uploading && (
                  <div className="mb-3 h-2 w-full overflow-hidden rounded-full" style={{ background: "var(--green-100)" }}>
                    <div data-testid="photo-progress" style={{ width: `${progress}%`, background: "var(--green-800)" }} className="h-full transition-all" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { setPending(null); setError(""); }} disabled={uploading} data-testid="photo-retake" className="flex items-center justify-center gap-2 rounded-full border-2 py-3 font-heading text-[14px] font-bold disabled:opacity-50" style={{ borderColor: "var(--line)", color: "var(--green-900)" }}><RotateCcw size={16} /> Retake</button>
                  <button onClick={submit} disabled={uploading} data-testid="photo-submit" className="flex items-center justify-center gap-2 rounded-full py-3 font-heading text-[14px] font-bold text-white disabled:opacity-60" style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>{uploading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {uploading ? `${progress}%` : "Submit"}</button>
                </div>
              </div>
            )}
            {error && <p className="text-[13px] font-semibold" data-testid="photo-error" style={{ color: "var(--orange-700)" }}>{error}</p>}

            <div>
              <p className="mb-2 font-heading text-[13px] font-bold" style={{ color: "var(--green-950)" }}>Submitted for this product</p>
              {submitted.length === 0 ? (
                <p className="text-[13px]" style={{ color: "var(--muted)" }}>No photos submitted yet.</p>
              ) : (
                <div className="grid gap-3" data-testid="photo-submitted">
                  {grouped.filter((g) => g.items.length).map((g) => (
                    <div key={g.id}>
                      <p className="mb-1 text-[12px] font-bold" style={{ color: "var(--muted)" }}>{g.label}{g.private ? " · private" : ""} ({g.items.length})</p>
                      <div className="grid grid-cols-3 gap-2">
                        {g.items.map((s) => (
                          <div key={s.id} className="relative overflow-hidden rounded-lg border" style={{ borderColor: "var(--line)" }}>
                            <img src={`${API}/photos/file/${s.id}?variant=web&t=${encodeURIComponent(token)}`} alt={g.label} className="h-24 w-full object-cover" />
                            {s.status === "published" && <span className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[9px] font-extrabold text-white" style={{ background: "var(--green-800)" }}>LIVE</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {toast && <div data-testid="photo-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-full px-5 py-2.5 font-heading text-[14px] font-bold text-white shadow-lg" style={{ background: "var(--green-800)" }}><CheckCircle2 size={16} className="mr-1 inline" /> {toast}</div>}
    </div>
  );
}
